import { makeStyles } from "@fluentui/react-components";
import { IM365EntraIDSectionProps } from "./m365-entra-id-section.props";
import { usePPA } from "context/ppa-context";
import { useEffect, useState } from "react";
import { IM365EntraIDPermissions } from "@interfaces/settings/permissions.interfaces";
import { IApiError } from "@interfaces/common.interfaces";
import { getError } from "lib/utils";
import ScrollPanel from "components/layout/scroll-panel/scroll-panel";
import LoadingSpinner from "components/common/loading-spinner/loading-spinner";
import LoadingErrorBar from "components/common/loading-error-bar/loading-error-bar";
import M365EntraIDTile from "../m365-entra-id-tile/m365-entra-id-tile";
import { differenceInSeconds, parseISO } from "date-fns";


const layoutStyles = makeStyles({
    mainWrapper: {
        margin: '1em 1.5em',
        maxWidth: '600px'
    }
});

export default function M365EntraIDSection(props: IM365EntraIDSectionProps) {
    const styles = layoutStyles();
    const { getApiClient } = usePPA();
    const [loading, setLoading] = useState<boolean>(false);
    const [apiError, setApiError] = useState<IApiError | null>(null);
    const [consented, setConsented] = useState<string>('');
    const [checkCount, setCheckCount] = useState<number>(0);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [m365EntraIDPermissions, setM365EntraIDPermissions] = useState<IM365EntraIDPermissions>();

    const getM365EntraIDPermissions = async (): Promise<IM365EntraIDPermissions> => {
        try {
            const response = await getApiClient().get<IM365EntraIDPermissions>(`/api/settings/permissions/m365-entra-id`);
            return response.data;
        }
        catch (ex: unknown) {
            throw ex;
        }
    }

    const loadM365EntraIDPermissions = (showLoading: boolean) => {
        setLoading(showLoading);
        getM365EntraIDPermissions().then(
            (roles: IM365EntraIDPermissions) => {
                setM365EntraIDPermissions(roles);
                setLoading(false);
                setCheckCount(checkCount + 1);
                setRefreshing(false);
            },
            (error: any) => {
                setApiError(getError(error));
                setLoading(false);
                setRefreshing(false);
            });
    }

    useEffect(() => {
        loadM365EntraIDPermissions(true);
    }, []);

    const setConsentCallback = () => {
        setCheckCount(0);
    }

    const setConsentedCallback = (datetime: string) => {
        setConsented(datetime);
    }

    const refreshCallback = () => {
        setRefreshing(true);
        loadM365EntraIDPermissions(false);
    }

    useEffect(() => {
        var timerId: NodeJS.Timeout;
        if (consented) {
            const consentedDateTime = parseISO(consented);
            const diff = differenceInSeconds(new Date(), consentedDateTime);
            const timedOut = diff > 30;

            // If there are resources and permissions, check if any are either not required ot not consented.
            // If so, keep checking the permission until they all come back as required and consented or the timeout is reached.
            var keepChecking = false;
            m365EntraIDPermissions?.resources.forEach((r) => { r.permissions.forEach((p) => { if (!p.required || !p.consented) { keepChecking = true } }) })

            if ((!keepChecking && checkCount > 0) || timedOut) {
                setConsented('');
                setCheckCount(0);
                setRefreshing(false);
            }
            else {
                timerId = setTimeout(() => {
                    loadM365EntraIDPermissions(false);
                }, 5000);
            }
        }
        return () => clearTimeout(timerId);
    }, [consented, checkCount]);

    return (
        <ScrollPanel
            fixed={<>
                <LoadingSpinner loading={loading} />
            </>}
            scroll={
                <div className={styles.mainWrapper}>
                    <LoadingErrorBar error={apiError} />
                    {!loading && <M365EntraIDTile
                        m365EntraIDPermissions={m365EntraIDPermissions}
                        setConsentCallback={setConsentCallback}
                        setConsentedCallback={setConsentedCallback}
                        refreshCallback={refreshCallback}
                        checking={!!consented || refreshing} />}
                </div>
            }
        />
    );
}